import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useId } from "react";
import { Button, Col, Row, Stack } from "react-bootstrap";
import { Tooltip } from "react-tooltip";
import { InfoCircleOutline } from "./InfoCircleOutline";

export interface PageTitleAction {
  title: string;
  onClick: any;
  color?: string;
  disabled?: boolean;
}

interface PageTitleState {
  title: string | undefined;
  buttonTitle?: string;
  onButtonClick?: any;
  buttonDisabled?: boolean;
  buttonColor?: string;
  type?: string;
  descriptionText?: string;
  subTitleText?: string;
  backButtonText?: string;
  onBackClick?: any;
  actions?: PageTitleAction[];
}

const PageTitle = ({
  title,
  buttonTitle,
  onButtonClick,
  buttonDisabled = false,
  buttonColor = "primary",
  type = "page",
  descriptionText,
  subTitleText,
  backButtonText,
  onBackClick,
  actions,
}: PageTitleState) => {
  const descriptionTooltipId = `page-title-description-${useId()}`;

  // buttonTitle/onButtonClick are shorthand for a single action; actions wins when both are given
  const resolvedActions: PageTitleAction[] =
    actions && actions.length > 0
      ? actions
      : onButtonClick
        ? [{ title: buttonTitle ?? "", onClick: onButtonClick, color: buttonColor, disabled: buttonDisabled }]
        : [];
  const hasMultipleActions = resolvedActions.length > 1;

  return (
    <div>
      {backButtonText && onBackClick && (
        <Stack direction="horizontal" gap={2} className="cursor-pointer" onClick={onBackClick}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-0 mb-1 app-tertiary-color-inverse cursor-pointer" fontSize={16} />
          <h6 className="app-tertiary-color-inverse">{backButtonText}</h6>
        </Stack>
      )}
      <Row className="pe-2">
        <Col className={hasMultipleActions ? "" : resolvedActions.length === 1 ? "col-8" : "col-12"}>
          {type === "page" ? (
            <>
              <h2 className="hero-heading app-header-text">
                {title}
                {descriptionText && (
                  <FontAwesomeIcon
                    icon={InfoCircleOutline}
                    className="ms-2 mb-1 app-primary-color-inverse"
                    fontSize={16}
                    data-tooltip-id={descriptionTooltipId}
                  />
                )}
              </h2>
            </>
          ) : type === "section-sm" ? (
            <h5 className="hero-heading app-header-text">
              {title}
              {descriptionText && (
                <FontAwesomeIcon
                  icon={InfoCircleOutline}
                  className="ms-2 align-middle app-primary-color-inverse"
                  fontSize={14}
                  data-tooltip-id={descriptionTooltipId}
                />
              )}
            </h5>
          ) : type === "sub-section" ? (
            <h6 className="hero-heading app-header-text">{title}</h6>
          ) : (
            <h4 className="hero-heading app-header-text ">
              {title}
              {descriptionText && (
                <FontAwesomeIcon
                  icon={InfoCircleOutline}
                  className="ms-2 mb-1 app-primary-color-inverse"
                  fontSize={16}
                  data-tooltip-id={descriptionTooltipId}
                />
              )}
            </h4>
          )}
          {/* rendered outside the heading so it does not inherit the heading's font */}
          {descriptionText && (
            <Tooltip
              id={descriptionTooltipId}
              place="right"
              style={{ padding: "5px 10px", zIndex: "1100", whiteSpace: "pre-line", textAlign: "center" }}
            >
              {descriptionText}
            </Tooltip>
          )}
        </Col>
        {resolvedActions.length > 0 && (
          <Col className={hasMultipleActions ? "col-auto pe-0" : "text-end col-4 pe-0"}>
            <Stack direction="horizontal" gap={3} className="justify-content-end">
              {resolvedActions.map((action, index) => (
                <React.Fragment key={action.title}>
                  {index > 0 && <div className="vr mb-1" />}
                  <Button
                    data-testid={`pagetitle-button-${action.title}`}
                    id={index === 0 ? "pagetitle-button" : undefined}
                    className={
                      action.color === "primary" || action.color === undefined
                        ? "app-primary-color button"
                        : action.color === "secondary"
                          ? "app-secondary-color button"
                          : action.color === "tertiary"
                            ? "app-tertiary-color button"
                            : "btn-secondary button"
                    }
                    onClick={action.onClick}
                    disabled={action.disabled}
                  >
                    {action.title}
                  </Button>
                </React.Fragment>
              ))}
            </Stack>
          </Col>
        )}
      </Row>
      {subTitleText && (
        <p style={{ whiteSpace: "pre-wrap" }} className="text-muted m-0 small">
          {subTitleText}
        </p>
      )}
    </div>
  );
};

export default PageTitle;
